import { useCallback } from 'react';
import { useTheme as useThemeContext } from '@/contexts/ThemeContext';
import type { ThemeMode } from '@/types/common';

interface ThemeHook {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode | ((prev: ThemeMode) => ThemeMode)) => void;
  toggleTheme: () => void;
  isDarkMode: () => boolean;
  isSystemDark: () => boolean;
}

export function useTheme(): ThemeHook {
  const { theme, setTheme } = useThemeContext();

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      switch (current) {
        case 'light':
          return 'dark';
        case 'dark':
          return 'system';
        case 'system':
          return 'light';
        default:
          return 'light';
      }
    });
  }, [setTheme]);

  const isSystemDark = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  const isDarkMode = useCallback(() => {
    if (theme === 'system') {
      return isSystemDark();
    }
    return theme === 'dark';
  }, [theme, isSystemDark]);

  const setThemeWithPreference = useCallback((newTheme: ThemeMode | ((prev: ThemeMode) => ThemeMode)) => {
    if (typeof newTheme === 'function') {
      setTheme(newTheme);
    } else {
      if (newTheme === 'system') {
        // Remove any manual theme preference
        localStorage.removeItem('theme');
      } else {
        // Save manual theme preference
        localStorage.setItem('theme', newTheme);
      }
      setTheme(newTheme);
    }
  }, [setTheme]);

  return {
    theme,
    setTheme: setThemeWithPreference,
    toggleTheme,
    isDarkMode,
    isSystemDark,
  };
}

export default useTheme;